// IE 2026 09 23 / ODOO-5800 (Igor: «форма легенды должна переноситься пользователем»): ПЕРЕНОС ВСПЛЫВАЮЩЕЙ ФОРМЫ ЗА ШАПКУ — pointer events с захватом указателя
// (setPointerCapture: перенос не рвётся, когда курсор уходит с шапки / из окна), по образцу pinDrag_* закреплённой формы ячейки (Dropdown.vue), без изменения размера.
// Форма — position: absolute в положенном (position: relative) контейнере, исходное место — top/right по CSS; после переноса — явные left/top от offsetParent
// (right/bottom: auto — иначе left + right растягивают форму). Положение хранит КОМПОНЕНТ в data (onMove → legendPos, legendDrag_style → :style), поэтому оно
// переживает перерисовки, пока форма открыта; сброс при закрытии / повторном открытии — дело компонента. Форма не уходит за окно и за видимую область ближайших
// предков с прокруткой / обрезкой (там её всё равно не видно); если форма больше видимой области — держится её верхний / левый край (шапка видна).
// Начало на поле ввода / флажке / кнопке / ссылке / значке (LEGEND_DRAG_SKIP) — не перенос: их клики доходят как обычно.
//
// Использование (Dropdown.vue, AttrAnalysis.vue):
//   шапка формы   @pointerdown="legendGrab($event)"  →  legendDrag_start(e, e.currentTarget.parentElement, (pos) => { this.legendPos = pos })
//   сама форма    :style="legendPosStyle"            →  computed: legendDrag_style(this.legendPos)
//   CSS шапки     cursor: move; touch-action: none; user-select: none;  на время переноса body несёт класс legend-dragging (курсор move поверх всего)

export const LEGEND_DRAG_SKIP = 'button, a, input, textarea, select, label, [contenteditable], .pi, .p-button, .p-checkbox, .p-dropdown'
export const LEGEND_DRAG_CLASS = 'legend-dragging'

// стиль формы для сохранённого положения; null / без чисел — исходное место по CSS
export function legendDrag_style(pos) {
    if (!pos || !Number.isFinite(pos.left) || !Number.isFinite(pos.top)) return null
    return { left: Math.round(pos.left) + 'px', top: Math.round(pos.top) + 'px', right: 'auto', bottom: 'auto' }
}

// видимая область для формы: окно ∩ ближайшие предки с overflow ≠ visible (прокрутка / обрезка)
function legendDrag_clip(pop) {
    let left = 0, top = 0, right = window.innerWidth, bottom = window.innerHeight
    for (let el = pop.parentElement; el && el !== document.body && el !== document.documentElement; el = el.parentElement) {
        let cs
        try { cs = getComputedStyle(el) } catch (err) { break }
        if (cs.overflowX === 'visible' && cs.overflowY === 'visible') continue
        const r = el.getBoundingClientRect()
        left = Math.max(left, r.left); top = Math.max(top, r.top); right = Math.min(right, r.right); bottom = Math.min(bottom, r.bottom)
    }
    return { left, top, right, bottom }
}

// IE 2026 10 22 / ODOO-5800 (CellLegendPop): видимая часть элемента — его прямоугольник ∩ окно ∩ предки с overflow ≠ visible (блок формы может быть прокручен внутри
// формы ячейки: первое место формы легенды — в видимой части блока, не над формой); null — элемент не виден
export function legendDrag_visibleRect(el) {
    if (!el || typeof el.getBoundingClientRect !== 'function') return null
    const r = el.getBoundingClientRect()
    let left = r.left, top = r.top, right = r.right, bottom = r.bottom
    const c = legendDrag_clip(el)
    left = Math.max(left, c.left); top = Math.max(top, c.top); right = Math.min(right, c.right); bottom = Math.min(bottom, c.bottom)
    return (right > left && bottom > top) ? { left, top, right, bottom } : null
}

// pointerdown на шапке: pop — переносимая форма (position: absolute), onMove(pos) получает {left, top} в px от offsetParent на каждом шаге и в конце;
// opts.skip — селектор элементов, с которых перенос не начинается (по умолчанию LEGEND_DRAG_SKIP); opts.onEnd — по окончании; opts.origin {left, top} — исходное
// положение формы position: fixed (CellLegendPop: координаты экрана, ограничение — окно). Возвращает true, если перенос начат
export function legendDrag_start(e, pop, onMove, opts = {}) {
    if (!e || e.button !== 0 || !pop || typeof onMove !== 'function') return false
    const t = e.target
    if (t && t.closest && t.closest(opts.skip || LEGEND_DRAG_SKIP)) return false
    const handle = e.currentTarget || pop
    const o = opts.origin && Number.isFinite(opts.origin.left) && Number.isFinite(opts.origin.top) ? opts.origin : null   // IE 2026 10 22 / ODOO-5800: форма position: fixed (CellLegendPop) — исходное место от компонента, не offsetLeft/Top
    const d = { id: e.pointerId, sx: e.clientX, sy: e.clientY, left: o ? o.left : pop.offsetLeft, top: o ? o.top : pop.offsetTop, box: pop.getBoundingClientRect(), clip: legendDrag_clip(pop), raf: null, last: null, done: false }
    const apply = () => {
        d.raf = null
        const ev = d.last; if (!ev) return
        // сдвиг ограничен так, чтобы форма осталась в видимой области; форма больше области — держится её верхний / левый край
        const dx = Math.max(d.clip.left - d.box.left, Math.min(ev.clientX - d.sx, d.clip.right - d.box.right))
        const dy = Math.max(d.clip.top - d.box.top, Math.min(ev.clientY - d.sy, d.clip.bottom - d.box.bottom))
        onMove({ left: Math.round(d.left + dx), top: Math.round(d.top + dy) })
    }
    const move = (ev) => { if (ev.pointerId !== d.id) return; d.last = ev; ev.preventDefault(); if (!d.raf) d.raf = requestAnimationFrame(apply) }
    const end = (ev) => {
        if (d.done || (ev && ev.pointerId !== undefined && ev.pointerId !== d.id)) return
        d.done = true
        window.removeEventListener('pointermove', move); window.removeEventListener('pointerup', end); window.removeEventListener('pointercancel', end)
        handle.removeEventListener('lostpointercapture', end)
        if (d.raf) { cancelAnimationFrame(d.raf); apply() }
        try { if (handle.hasPointerCapture && handle.hasPointerCapture(d.id)) handle.releasePointerCapture(d.id) } catch (err) { /* захват уже снят */ }
        document.body.classList.remove(LEGEND_DRAG_CLASS)
        if (typeof opts.onEnd === 'function') opts.onEnd()
    }
    try { handle.setPointerCapture(d.id) } catch (err) { /* без захвата — события идут по окну, как у pinDrag_* */ }
    window.addEventListener('pointermove', move, { passive: false })
    window.addEventListener('pointerup', end, { passive: false })
    window.addEventListener('pointercancel', end, { passive: false })
    handle.addEventListener('lostpointercapture', end)
    document.body.classList.add(LEGEND_DRAG_CLASS)
    e.preventDefault()
    return true
}

// IE 2026 10 22 / ODOO-5800 (Igor 22.10: «вынести легенду за рамки формы ячейки»): форма легенды в слое над формой (CellLegendPop.vue, Teleport в body, position: fixed)
export const LEGEND_POP_Z = 10000   // как .dropdown-handles (ручки размера закреплённой формы) — выше формы ячейки (9999) и линии привязки
// первое положение формы в координатах экрана — правый верх блока (host — прямоугольник блока, где стоит компонент), в пределах окна win {w, h}; size — размер формы
export function legendPop_place(host, size, win, offsetTop = 4, offsetRight = 6) {
    const w = Math.max(0, size && size.w || 0), h = Math.max(0, size && size.h || 0)
    const W = win && win.w || 0, H = win && win.h || 0
    const r = host || { left: 0, top: 0, right: W }
    let left = r.right - offsetRight - w, top = r.top + offsetTop
    left = Math.max(0, Math.min(left, W - w)); top = Math.max(0, Math.min(top, H - h))
    return { left: Math.round(left), top: Math.round(top) }
}

export default { legendDrag_start, legendDrag_style, legendDrag_visibleRect, legendPop_place, LEGEND_POP_Z, LEGEND_DRAG_SKIP, LEGEND_DRAG_CLASS }
